//frontend/src/components/user/Register.js
import React, { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { register, clearAuthError } from '../../actions/userActions';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, User, Lock, Eye, EyeOff, Upload, Camera, Phone, MapPin } from 'lucide-react';
import MetaData from '../layouts/MetaData';

const InputField = ({ icon: Icon, label, id, type, value, onChange, placeholder, name, required = true, endIcon, pattern, maxLength, onKeyPress }) => (
    <div className="space-y-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
            {label}
        </label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon className="h-4 w-4 text-[#dc2626]" />
            </div>
            <input
                type={type}
                id={id}
                name={name}
                className="h-10 block w-full pl-10 pr-10 text-center text-gray-700 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#dc2626] focus:border-transparent transition-all duration-300 placeholder:text-gray-400 placeholder:text-center text-sm"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                pattern={pattern}
                maxLength={maxLength}
                onKeyPress={onKeyPress}
            />
            {endIcon}
        </div>
    </div>
);

const TextAreaField = ({ icon: Icon, label, id, value, onChange, placeholder, name, required = true }) => (
    <div className="space-y-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
            {label}
        </label>
        <div className="relative">
            <div className="absolute top-3 left-3 pointer-events-none">
                <Icon className="h-4 w-4 text-[#dc2626]" />
            </div>
            <textarea
                id={id}
                name={name}
                className="block w-full pl-10 pr-3 py-2 text-center text-gray-700 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#dc2626] focus:border-transparent transition-all duration-300 resize-none h-16 placeholder:text-gray-400 placeholder:text-center text-sm"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
            />
        </div>
    </div>
);

export default function Register() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [userData, setUserData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [avatar, setAvatar] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('/images/default_avatar.png');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { loading, error, isAuthenticated } = useSelector(state => state.authState);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
        if (error) {
            setIsSubmitting(false);
            toast.error(error, {
                onOpen: () => dispatch(clearAuthError())
            });
        }
    }, [error, dispatch, isAuthenticated, navigate]);

    const onChange = e => {
        if (e.target.name === 'avatar') {
            const file = e.target.files[0];
            if (!file) return;

            // Check file size
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                if (reader.readyState === 2) {
                    setAvatarPreview(reader.result);
                    setAvatar(file);
                }
            };
            reader.readAsDataURL(file);
        } else {
            setUserData({ ...userData, [e.target.name]: e.target.value });
        }
    };

    const handleNumberInput = (e) => {
        const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
        if (onlyNumbers.length <= 10) {
            setUserData({ ...userData, phone: onlyNumbers });
        }
    };

    const preventNonNumeric = (e) => {
        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
            e.preventDefault();
        }
    };

    const submitHandler = e => {
        e.preventDefault();
        
        // Prevent multiple submissions
        if (isSubmitting) return;
        setIsSubmitting(true);
        
        // Validate phone number
        if (!userData.phone || userData.phone.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            setIsSubmitting(false);
            return;
        }
        
        // Validate address
        if (!userData.address.trim()) {
            toast.error('Please enter your address');
            setIsSubmitting(false);
            return;
        }
        
        // Validate password
        if (!userData.password || userData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            setIsSubmitting(false);
            return;
        }

        const formData = new FormData();
        formData.append('name', userData.name);
        formData.append('email', userData.email);
        formData.append('password', userData.password);
        formData.append('phone', userData.phone);
        formData.append('address', userData.address);
        
        if (avatar) {
            formData.append('avatar', avatar);
        }
        
        dispatch(register(formData))
            .then(() => {
                // Registration success will be handled by the useEffect for isAuthenticated
            })
            .catch((err) => {
                setIsSubmitting(false);
                console.error("Registration error:", err);
            });
    };

    return (
        <Fragment>
            <MetaData title="Create Account - Meat Buddy" />
            <div 
                className="min-h-screen flex items-center justify-center py-4 px-4"
                style={{
                    backgroundImage: `url('https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-background-image.png')`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-lg w-full">
                    <div className="text-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Welcome to Meat Buddy</h2>
                        <h1 className="text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                            Create Your Account
                        </h1>
                        <p className="mt-1 text-gray-700 text-sm">
                            Join Meat Buddy for fresh, quality meat delivery
                        </p>
                    </div>
                    <form onSubmit={submitHandler} className="space-y-3" encType="multipart/form-data">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <InputField
                                icon={User}
                                label="Full Name"
                                id="name_field"
                                type="text"
                                value={userData.name}
                                onChange={onChange}
                                placeholder="Enter your full name"
                                name="name"
                            />
                            <InputField
                                icon={Mail}
                                label="Email Address"
                                id="email_field"
                                type="email"
                                value={userData.email}
                                onChange={onChange}
                                placeholder="Enter your email"
                                name="email"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <InputField
                                icon={Phone}
                                label="Phone Number"
                                id="phone_field"
                                type="tel"
                                value={userData.phone}
                                onChange={handleNumberInput}
                                onKeyPress={preventNonNumeric}
                                placeholder="Enter 10-digit phone number"
                                name="phone"
                                pattern="[0-9]{10}"
                                maxLength="10"
                            />
                            <InputField
                                icon={Lock}
                                label="Password"
                                id="password_field"
                                type={showPassword ? "text" : "password"}
                                value={userData.password}
                                onChange={onChange}
                                placeholder="Create a strong password"
                                name="password"
                                endIcon={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ?
                                            <EyeOff className="h-4 w-4 text-gray-400 hover:text-[#dc2626] transition-colors duration-200" /> :
                                            <Eye className="h-4 w-4 text-gray-400 hover:text-[#dc2626] transition-colors duration-200" />
                                        }
                                    </button>
                                }
                            />
                        </div>
                        <TextAreaField
                            icon={MapPin}
                            label="Address"
                            id="address_field"
                            value={userData.address}
                            onChange={onChange}
                            placeholder="Enter your full address"
                            name="address"
                        />
                        <div className="flex items-center space-x-4">
                            <div className="w-20 h-20 relative flex-shrink-0">
                                <img
                                    src={avatarPreview}
                                    alt="Avatar Preview"
                                    className="w-full h-full rounded-full object-cover border-2 border-gray-300 shadow-md"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Camera className="h-6 w-6 text-gray-700 opacity-0 hover:opacity-100 transition-opacity duration-300" />
                                </div>
                            </div>
                            <div className="flex-1 space-y-1">
                                <label className="block text-xs font-medium text-gray-700">
                                    Profile Picture (Optional)
                                </label>
                                <label className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 text-xs">
                                    <Upload className="h-4 w-4 text-[#dc2626] mr-2" />
                                    <span className="text-gray-700">Choose Photo</span>
                                    <input
                                        type="file"
                                        name="avatar"
                                        accept="image/*"
                                        onChange={onChange}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] disabled:opacity-50 disabled:cursor-not-allowed h-10 items-center"
                            disabled={isSubmitting || loading}
                        >
                            {(isSubmitting || loading) ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating Account...
                                </span>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                        <div className="text-center text-sm text-gray-700">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="font-medium text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200"
                            >
                                Login here
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </Fragment>
    );
}